import { Layers, Plus, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AddCopyItemDefaultsGrid } from "@/components/catalog/AddCopyItemDefaultsGrid";
import { JournalHoldingCard } from "@/components/catalog/JournalHoldingCard";
import type { CreateCatalogRequest } from "@/lib/api/catalog";
import type { JournalCatalogOptions } from "@/pages/admin/catalog/useJournalCatalogOptions";
import {
  createEmptyHolding,
  type JournalHoldingEntry,
} from "@/pages/admin/catalog/journal-catalog-form";
import { cn } from "@/lib/utils";

type ItemDefaults = CreateCatalogRequest["itemDefaults"];

const ACQUISITION_MODES = [
  { code: "PURCHASE", label: "Purchase" },
  { code: "DONATION", label: "Donation" },
  { code: "GIFT", label: "Gift" },
  { code: "EXCHANGE", label: "Exchange" },
  { code: "TETFUND", label: "TETFund" },
] as const;

interface JournalHoldingsEditorProps {
  holdings: JournalHoldingEntry[];
  onChange: (holdings: JournalHoldingEntry[]) => void;
  itemDefaults: ItemDefaults;
  onItemDefaultsChange: (defaults: ItemDefaults) => void;
  errors: Record<string, string>;
  options: JournalCatalogOptions;
}

export function JournalHoldingsEditor({
  holdings,
  onChange,
  itemDefaults,
  onItemDefaultsChange,
  errors,
  options,
}: JournalHoldingsEditorProps) {
  return (
    <div className="space-y-6">
      <Card className="overflow-hidden bg-white shadow-sm">
        <div className="space-y-4 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Layers className="h-5 w-5 text-primary" />
            Shared copy defaults
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <AddCopyItemDefaultsGrid
              copies={1}
              onCopiesChange={() => undefined}
              itemDefaults={itemDefaults}
              onItemDefaultsChange={onItemDefaultsChange}
              itemTypes={options.itemTypes}
              locations={options.locations}
              conditions={options.conditions}
              itemStatuses={options.itemStatuses}
              hideCopies
            />
            <div>
              <Label className="flex items-center gap-1.5">
                <ShoppingCart className="h-4 w-4 text-gray-400" />
                Acquisition mode <span className="text-red-500">*</span>
              </Label>
              <Select
                value={itemDefaults.acquisitionMode || ""}
                onValueChange={(value) =>
                  onItemDefaultsChange({
                    ...itemDefaults,
                    acquisitionMode: value || undefined,
                  })
                }
              >
                <SelectTrigger
                  className={cn("mt-1.5", errors.acquisitionMode && "border-red-500")}
                  aria-invalid={Boolean(errors.acquisitionMode)}
                >
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  {ACQUISITION_MODES.map((mode) => (
                    <SelectItem key={mode.code} value={mode.code}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.acquisitionMode ? (
                <p className="mt-1 text-sm text-red-600">{errors.acquisitionMode}</p>
              ) : null}
            </div>
          </div>
        </div>
      </Card>
      {holdings.map((holding, index) => (
        <JournalHoldingCard
          key={holding.key}
          index={index}
          holding={holding}
          canRemove={holdings.length > 1}
          errors={errors}
          options={options}
          itemDefaults={itemDefaults}
          onChange={(next) => onChange(holdings.map((entry, i) => (i === index ? next : entry)))}
          onRemove={() => onChange(holdings.filter((_, i) => i !== index))}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() => onChange([...holdings, createEmptyHolding()])}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add another volume
      </Button>
    </div>
  );
}
